import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { Modal } from 'react-bootstrap'
import { CloseIcon, useErrorService } from '@abroad/components'
import API from '../../utils/API'
import Labels from '../../constants/labels'
import { getSortedTimezones } from '../../utils/timezone'
import { SelectionField } from '../common'
import ScheduleDatePicker from './ScheduleDatePicker'
import ScheduleTimeSlots from './ScheduleTimeSlots'

const DEFAULT_PREVIEW_DURATION = '60'

const AvailabilityPreviewModal = ({ show, onHide, timezone }) => {
  const Error = useErrorService()
  const timezoneOptions = useMemo(() => getSortedTimezones(), [])

  const [previewTimezone, setPreviewTimezone] = useState(timezone)
  const [duration, setDuration] = useState(DEFAULT_PREVIEW_DURATION)
  const [currentMonth, setCurrentMonth] = useState(moment().startOf('month'))
  const [availableDays, setAvailableDays] = useState({})
  const [selectedDate, setSelectedDate] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const requestIdRef = useRef(0)

  useEffect(() => {
    setPreviewTimezone(timezone)
  }, [timezone])

  useEffect(() => {
    if (!show || !previewTimezone) {
      return
    }

    const requestId = ++requestIdRef.current

    const loadMonthSlots = async () => {
      setIsLoading(true)
      try {
        const { data } = await API.CoachAPI.coachUser.getAvailableSlotsPreview({
          year: currentMonth.year(),
          month: currentMonth.month() + 1,
          duration: Number(duration),
          timezone: previewTimezone,
        })
        if (requestId !== requestIdRef.current) return

        const daysMap = (data?.days || []).reduce((acc, day) => {
          if (day?.date) {
            acc[day.date] = day
          }
          return acc
        }, {})
        const firstDayWithSlots = Object.values(daysMap).find(
          (day) => day?.slots?.length,
        )

        setAvailableDays(daysMap)
        setSelectedDate((prev) =>
          prev && daysMap[prev]?.slots?.length
            ? prev
            : firstDayWithSlots?.date || null,
        )
      } catch (error) {
        if (requestId !== requestIdRef.current) return
        setAvailableDays({})
        setSelectedDate(null)
        Error.showError(error)
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false)
        }
      }
    }

    loadMonthSlots()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentMonth, duration, previewTimezone])

  const handleMonthChange = useCallback((month) => {
    if (!month) {
      return
    }

    const normalizedMonth = moment(month).startOf('month')
    setCurrentMonth((prev) =>
      prev && prev.isSame(normalizedMonth, 'month') ? prev : normalizedMonth,
    )
  }, [])

  const timeSlots = availableDays[selectedDate]?.slots || []

  return (
    <Modal show={show} onHide={onHide} size='xl' centered>
      <Modal.Header className='position-relative'>
        <Modal.Title>
          <h3 className='s3 mb-1'>See my availability</h3>
          <p className='sa5 mb-0 font-weight-normal'>
            This is how your open time slots appear to clients when they book a
            session.
          </p>
        </Modal.Title>
        <div
          role='button'
          aria-label='Close'
          title='Close'
          className='rounded-circle quest-close-icon position-absolute p-2'
          style={{
            zIndex: 10,
            cursor: 'pointer',
            top: 12,
            right: 12,
            transform: 'none',
          }}
          onClick={onHide}>
          <CloseIcon
            className='d-flex align-items-center justify-content-center'
            fontSize='10px'
            color='rgb(10, 34, 60)'
            discardMargin={true}
            fontWeight='bold'
          />
        </div>
      </Modal.Header>
      <Modal.Body className='p-3'>
        <div className='d-flex flex-column flex-md-row mb-3'>
          <div className='mr-md-3 mb-3 mb-md-0' style={{ minWidth: 200 }}>
            <label className='form-label'>Session Duration</label>
            <SelectionField
              options={Labels.sessionDuration}
              getKeyAsOption
              hidePlaceholder
              isUseFormikSelectionField={false}
              value={duration}
              onChange={(event) =>
                setDuration(event?.target?.value || DEFAULT_PREVIEW_DURATION)
              }
              disabled={isLoading}
            />
          </div>
          <div style={{ minWidth: 300 }}>
            <label className='form-label'>Client Time Zone</label>
            <SelectionField
              options={timezoneOptions}
              getValueAsOption
              optionplaceholder='Select time zone'
              isUseFormikSelectionField={false}
              value={previewTimezone}
              onChange={(event) =>
                setPreviewTimezone(event?.target?.value || timezone)
              }
              className='custom-timezone-select'
              disabled={isLoading}
            />
          </div>
        </div>

        <div
          className='border border-dark d-flex flex-column flex-md-row overflow-hidden'
          style={{ minHeight: 490, height: 490 }}>
          <div
            className='flex-fill p-3 border border-dark border-top-0 border-bottom-0 border-left-0 d-flex flex-column'
            style={{ minHeight: 0 }}>
            <ScheduleDatePicker
              currentMonth={currentMonth}
              selectedDate={selectedDate}
              onMonthChange={handleMonthChange}
              onDateSelect={setSelectedDate}
              availableDays={availableDays}
              disablePastDates
              disableDatesWithoutSlots
              isLoading={isLoading}
            />
          </div>
          <div
            className='flex-fill px-4 py-3 d-flex flex-column overflow-hidden'
            style={{ minHeight: 0 }}>
            <ScheduleTimeSlots
              slots={timeSlots}
              isLoading={isLoading}
              timezone={previewTimezone}
            />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

AvailabilityPreviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  timezone: PropTypes.string,
}

export default AvailabilityPreviewModal
